import {
  degToRad,
  line_line_intersect2,
  P_xytonum,
  uMath
} from "src/components/Planner/planner/uMath";
import {
  cleanupMethods,
  CMToPx,
  eventShouldntTriggerHotkey,
  getMotionSensorRadius,
  isPlannerBusy,
  isPlannerVisible,
  isSingleDevInIntendedRoom,
  setMode,
  toolbarSwitchSet
} from "src/components/Planner/planner/planner";
import { PlannerGlobals } from "src/components/Planner/planner/planner-globals";
import {
  getDeviceById,
  getRoomById,
  isRoomOutside,
  uDev,
  wallsOfRoom
} from "src/components/Planner/planner/uDev";
import * as d3 from "d3";
import $ from "jquery";
import {
  toastInfo,
  toastInfoSingle,
  toastRemoveAll
} from "src/components/Planner/controller";
import {ts} from "src/utils/translate";
import {
  hasPermissionForFuncInRole
} from "src/utils/controllerHasPermissionsAdapter";

import { MovsenGlobals } from "src/components/Planner/planner/movsen-globals";

const MOVSEN_PRECISION = 1; // must be divisor of 360:  1, 2, 3, 4, 5, 6, 8, 9, 10, 12, 15, 18, 20, 24, 30, 36, 40, 45, 60, 72, 90, 120, 180, 360

const motion_sensor_props = {
  81: { angle: 110 },
  82: { angle: 110 },
  154: { angle: 110 },
  155: { angle: 110 },
  156: { angle: 110 },
  225: { angle: 110 },
  226: { angle: 110 },
  227: { angle: 110 },
  228: { angle: 110 },
  24: { angle: 110 },
  25: { angle: 110 },
  251: { angle: 110 },
  309: { angle: 110 },
  310: { angle: 110 },
  313: { angle: 110 },
  314: { angle: 110 },
  324: { angle: 110 },
  328: { angle: 110 },
  413: { angle: 110 },
};


/**
 * https://en.wikipedia.org/wiki/Angle_of_view
 * @param {number} sensorSizeMM
 * @param {number} focalLengthMM
 * @returns {number} Degrees
 */
export function getAngleOfView(sensorSizeMM, focalLengthMM) {
  return uMath.radToDeg(2 * Math.atan(sensorSizeMM / (2 * focalLengthMM)));
}

/**
 *
 * @param {Segment} radiusLine
 * @param {Wall[]} walls
 * @returns
 */
export function getRadiusLineWallIntersection(radiusLine, walls) {
  var allowedPx = CMToPx(8);
  var intersects = [];
  var radiusBegin = { x: radiusLine.x1, y: radiusLine.y1 };
  walls.forEach(wall => {
    var intersection = line_line_intersect2(radiusLine, wall);
    if (intersection) {
      var dist = uMath.pointDistance(radiusBegin, intersection);
      intersects.push({
        wall,
        dist,
        intersection
      })
    }
  });
  if (!intersects.length) {
    return false;
  }
  if (intersects.length === 1) {
    return intersects[0].intersection;
  }

  intersects.sort((a, b) => a.dist - b.dist);

  while (intersects.length >= 2) {
    var i1 = intersects[0];
    if (i1.wall.isOuterBorder) {
      break;
    }
    var i2 = intersects[1];
    var proj1 = uMath.getProjectedPointOnSegment(i2.wall, i1.intersection);
    var proj2 = uMath.getProjectedPointOnSegment(i1.wall, i2.intersection);
    var p1Len = uMath.pointDistance(i1.intersection, proj1);
    var p2Len = uMath.pointDistance(i2.intersection, proj2);

    if (p1Len < allowedPx && p2Len < allowedPx) {
      if (i2.wall.isOuterBorder) {
        intersects.splice(0, 1);
        break;
      }
      else {
        intersects.splice(0, 2);
      }
    }
    else {
      break;
    }
  }

  if (intersects.length) {
    return intersects[0].intersection;
  }
  else {
    return false;
  }
}

export function getCircleOutlinePoints(deviceId, radiusOverride = null) {
  var dev = getDeviceById(deviceId);
  var room = getRoomById(dev.roomId);
  var devCenter = P_xytonum(uDev.getCenterOfDevice_NoDOM(deviceId));

  if (!isSingleDevInIntendedRoom(dev)) {
    return [];
  }

  var radius = CMToPx(
      isCamera(dev) ?
          MovsenGlobals.camera_props[dev.product_name_id].radiusCM :
          getMotionSensorRadius(room.room_headroom, motion_sensor_props[dev.product_name_id].angle / 2)
  );
  if (radiusOverride !== null) {
    radius = CMToPx(radiusOverride);
  }
  var points = [];

  if (isCamera(dev)) {
    var halfAngle = MovsenGlobals.camera_props[dev.product_name_id].viewAngle / 2;
    for (var i = -halfAngle; i < halfAngle / MOVSEN_PRECISION; i++) {
      points.push(uDev.getAngleOfDevice(dev.deviceId) + i * MOVSEN_PRECISION);
    }
  }
  else {
    for (var i = 0; i < 360 / MOVSEN_PRECISION; i++) {
      points.push(i * MOVSEN_PRECISION);
    }
  }

  var allWalls;

  if (uDev.outerBorderExists()) {
    allWalls = PlannerGlobals.rooms.filter(function (room) { return !isRoomOutside(room, PlannerGlobals.outerBorder); })
    .map(function (room) { return wallsOfRoom(room) }).reduce(function (p, c) { return p.concat(c) }, []);
    var outerBorderWalls = uMath.segmentsFromPointArray(PlannerGlobals.outerBorder);
    outerBorderWalls.forEach(wall => wall.isOuterBorder = true);
    allWalls = allWalls.concat(outerBorderWalls);
  }
  else {
    allWalls = PlannerGlobals.rooms.map(function (room) { return wallsOfRoom(room) }).reduce(function (p, c) { return p.concat(c) }, []);
  }

  points = points
  .map(degToRad)
  .map(function (point) {
    var line = {
      x1: devCenter[0],
      y1: devCenter[1],
      x2: devCenter[0] + radius * Math.cos(point),
      y2: devCenter[1] + radius * Math.sin(point)
    };
    var newIntersectPt = getRadiusLineWallIntersection(line, allWalls);
    if (newIntersectPt) {
      line.x2 = newIntersectPt.x;
      line.y2 = newIntersectPt.y;
    }
    return [line.x2, line.y2];
  });

  if (isCamera(dev)) {
    points.unshift(devCenter);
  }

  return points;

}

/**
 *
 * @param {Device} dev
 */
export function drawDraggedMotionSensor(dev) {
  if (!PlannerGlobals.LENGTHOF1PX) {
    return;
  };
  setAllSensorsOff();

  d3.select('#motionsensors').remove();
  var radiiCont = PlannerGlobals.container.append("g").attr('class', 'motionsensors').attr('id', 'motionsensors');
  radiiCont.html('');

  if (!isMotionSensorOrCamera(dev)) { return; }

  var sensors = uDev.devicesOfRoom(dev.roomId).filter(function (x) {
    return isCamera(x) === isCamera(dev) && isMovementSensor(x) === isMovementSensor(dev);
  });

  drawPolyForSensors(radiiCont, sensors);

}

export function isMotionSensorOrCamera(dev) {
  return isMovementSensor(dev) || isCamera(dev);
}

export function isMovementSensor(dev) {
  if (typeof motion_sensor_props !== "object") { return false; }
  return dev.product_name_id && (dev.product_name_id in motion_sensor_props);
}

export function isProdCamera(treeProd) {
  if (typeof MovsenGlobals.camera_props !== "object") { return false; }
  var pnid = treeProd.id.replace('product_', '');
  return pnid in MovsenGlobals.camera_props;
}

export function isProdDreamMachine(treeProd) {
  var pnid = treeProd.id.replace('product_', '');
  return pnid == 346;
}

export function isCamera(dev) {
  if (typeof MovsenGlobals.camera_props !== "object") { return false; }
  return dev.product_name_id && (dev.product_name_id in MovsenGlobals.camera_props);
}

export function cleanupMotionSensorMode() {
  var radiiExist = d3.select('#motionsensors');
  if (radiiExist.node()) {
    setAllSensorsOff();
    radiiExist.remove();
  }
}

export function isMotionSensorMode() {
  return motionSensorModeState.camera || motionSensorModeState.movementSensor;
  /* var radiiExist = d3.select('#motionsensors');
  return !!radiiExist[0][0]; */
}

export function getDevicesForCurrentModes() {
  var devs = [];

  if (motionSensorModeState.camera) {
    devs = devs.concat(PlannerGlobals.devices.filter(isCamera));
  }

  if (motionSensorModeState.movementSensor) {
    devs = devs.concat(PlannerGlobals.devices.filter(isMovementSensor));
  }

  return devs;
}

export function redrawMotionSensors(force = false) {
  if (!force) {
    if (!PlannerGlobals.isInQuote) {
      var busyReason = isPlannerBusy();
      if (busyReason) {
        //toastInfoSingle(busyReason);
        return false;
      }
    }
  }

  var anyModeOn = motionSensorModeState.camera || motionSensorModeState.movementSensor;

  var sensorRadiusContainer = d3.select('#motionsensors');
  if (!anyModeOn) {
    sensorRadiusContainer.remove();
  }
  else {
    var sensors = getDevicesForCurrentModes();
    if (sensors.length) {
      //setMode.basic();
      sensorRadiusContainer.remove();
      var radiiCont = PlannerGlobals.container.append("g").attr('class', 'motionsensors').attr('id', 'motionsensors');
      drawPolyForSensors(radiiCont, sensors);
    }
    else {
      setMode.basic();
    }
  }
}

export function drawPolyForSensors(radiiCont, sensors) {
  radiiCont
  .selectAll('.radipoly')
  .data(sensors)
  .enter()
  .append('polygon')
  .attr('class', 'radipoly')
  .attr('points', function (d) {
    return getCircleOutlinePoints(d.deviceId).join(" ");
  })
  .attr('stroke', function (d) { return isCamera(d) ? '' : 'red'; })
  .attr('stroke-width', function (d) { return isCamera(d) ? "0px" : '0.1px'; })
  .attr('fill-opacity', function (d) { return isCamera(d) ? "0.4" : '0.3'; })
  .attr('style', function (d) { return 'fill:' + (isCamera(d) ? "#71b4ff" : "#bd1550") + ';pointer-events: none;'; })

  radiiCont
  .selectAll('.radipoly2')
  .data(sensors.filter(function (sensor) { return isCamera(sensor); }))
  .enter()
  .append('polygon')
  .attr('class', 'radipoly2')
  .attr('points', function (d) {
    return getCircleOutlinePoints(d.deviceId, 800).join(" ");
  })
  .attr('stroke-width', 0)
  .attr('fill-opacity', 0.3)
  .attr('style', 'fill:#a4ceff; pointer-events: none;')

}


const motionSensorModeState = {
  movementSensor: false,
  camera: false
}

/**
 *
 * @param {"movementSensor"|"camera"} toggledMode
 */
export function toggleMotionSensorsMode(toggledMode) {
  if (typeof PlannerGlobals.LENGTHOF1PX == 'undefined' || !PlannerGlobals.LENGTHOF1PX || !$('#svgAndChat').length || toggledMode == 'selectRoom') {
    return showNoMotionSensorError();
  };

  if (PlannerGlobals.mode !== "basic") {
    setMode.basic();
  }
  if (PlannerGlobals.LIGHTING_CALC) {
    PlannerGlobals.LIGHTING_CALC.stopLightCalcMode();
  }

  if (toggledMode === "camera" && !motionSensorModeState[toggledMode]) {
    var cameras = PlannerGlobals.devices.filter(isCamera);
    if (!cameras.length) {
      if (!PlannerGlobals.isInQuote) {
        toastInfoSingle(ts('p_movsen_nocameras', 'There are no security cameras placed.') );
      }
      return;
    }
  }
  else if (toggledMode === "movementSensor" && !motionSensorModeState[toggledMode]) {
    var movementSensors = PlannerGlobals.devices.filter(isMovementSensor);
    if (!movementSensors.length) {
      if (!PlannerGlobals.isInQuote) {
        toastInfoSingle(ts('p_movsen_nosensors', 'There are no motion sensors placed.') );
      }
      return;
    }
  }

  motionSensorModeState[toggledMode] = !motionSensorModeState[toggledMode];

  redrawMotionSensors();
  redrawMotionSensorButtonStates();
}

export function redrawMotionSensorButtonStates() {
  if (motionSensorModeState.camera) {
    toolbarSwitchSet("cameraradius-display", "on");
  }
  else {
    toolbarSwitchSet("cameraradius-display", "off");
  }

  if (motionSensorModeState.movementSensor) {
    toolbarSwitchSet("motionradius-display", "on");
    $('#toggle-movsen-btn').find("img").css("transform", "").css("background", "rgba(189, 21, 80, 0.3)");
  }
  else {
    toolbarSwitchSet("motionradius-display", "off");
    $('#toggle-movsen-btn').find("img").css("transform", "scale(0.8)rotate(90deg)").css("background", "");
  }
}

export function setAllSensorsOff() {
  motionSensorModeState.movementSensor = false;
  motionSensorModeState.camera = false;
  redrawMotionSensors();
  redrawMotionSensorButtonStates();
}

export function showNoMotionSensorError() {
  toastRemoveAll();
  if (!PlannerGlobals.isInQuote) {
    toastInfo(ts('p_mark_scale_please', "Without the floor scale marked, motion sensor radiuses can't be drawn. Try using the scale marker tool. (Hotkey: 2)").replace('2', '1'));
  }
  return;
}

export function initMovsen() {
  $(document).on('keyup', function (e) {
    if (eventShouldntTriggerHotkey(e) || !isPlannerVisible() ||
        !hasPermissionForFuncInRole("planner_sect.show_visu_tools", PlannerGlobals.userRoleInProject)) {
      return;
    }
    var pressedKey = e.key && e.key.toLowerCase();
    if (pressedKey === "m") {
      toggleMotionSensorsMode("movementSensor");
    }
    else if (pressedKey === "k") {
      toggleMotionSensorsMode("camera");
    }
  });

  cleanupMethods.push(function () {
    d3.select('#motionsensors').remove();
    setAllSensorsOff();
  });
}